import { useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import { recordWakeLockRelease, setWakeLockStatus } from "@common/lib/recordingTelemetry";

/**
 * Holds a Screen Wake Lock while `active` (EN-1383). Callers gate this to phones and tablets,
 * where the browser keeps recording after the screen times out but captures only silence, so
 * keeping the screen on is what preserves the audio. The browser drops the lock whenever the
 * page is hidden, so it is re-requested when the page becomes visible again. Failure is not an error: the lock is a
 * best-effort aid, and unsupported browsers simply record as before.
 */
const releaseSentinel = (sentinel: WakeLockSentinel): void => {
    sentinel.release().catch((error: unknown) => {
        Sentry.addBreadcrumb({
            category: "recording",
            message: "recording.wake_lock_release_failed",
            level: "info",
            data: { error: error instanceof Error ? error.name : String(error) },
        });
    });
};

const MAX_RELEASE_REACQUIRES = 3;

export type WakeLockUnavailableReason = "denied" | "unsupported" | "released";

const useScreenWakeLock = (active: boolean, onUnavailable?: (reason: WakeLockUnavailableReason) => void): void => {
    // Kept in a ref so a new callback identity each render does not restart the lock.
    const onUnavailableRef = useRef(onUnavailable);
    onUnavailableRef.current = onUnavailable;

    useEffect(() => {
        if (!active) return;
        // New active period. Not reset on cleanup: the upload reads the status after the recording stops.
        setWakeLockStatus(null);
        const wakeLock = typeof navigator !== "undefined" ? navigator.wakeLock : undefined;
        if (!wakeLock?.request) {
            setWakeLockStatus("unsupported");
            onUnavailableRef.current?.("unsupported");
            return;
        }

        let cancelled = false;
        let sentinel: WakeLockSentinel | null = null;
        let pending = false;
        // Tracked here rather than read from sentinel.released, which Chrome 84-86 does not implement.
        let held = false;
        let releaseReacquires = 0;
        // The caller is told at most once per activation that the screen cannot be kept on.
        let reportedUnavailable = false;
        const reportUnavailable = (reason: WakeLockUnavailableReason) => {
            if (reportedUnavailable || cancelled) return;
            reportedUnavailable = true;
            onUnavailableRef.current?.(reason);
        };

        const acquire = async () => {
            if (document.visibilityState !== "visible") return;
            // Prevent concurrent requests or overwriting a held lock
            if (pending || held) return;

            pending = true;
            try {
                const next = await wakeLock.request("screen");
                if (cancelled) {
                    releaseSentinel(next);
                    return;
                }
                sentinel = next;
                held = true;
                setWakeLockStatus("acquired");
                next.addEventListener("release", onSentinelRelease);
            } catch (error) {
                setWakeLockStatus("denied");
                reportUnavailable("denied");
                Sentry.addBreadcrumb({
                    category: "recording",
                    message: "recording.wake_lock_unavailable",
                    level: "info",
                    data: { error: error instanceof Error ? error.name : String(error) },
                });
            } finally {
                pending = false;
            }
        };

        // The browser can drop the lock on its own (low battery, hidden page). Our own cleanup sets
        // `cancelled` first, so intentional releases never land here. One re-request per release event.
        // A denied request is caught in acquire(), and a browser that grants then drops the lock over and
        // over is bounded by MAX_RELEASE_REACQUIRES; the counter resets when the page becomes visible.
        const onSentinelRelease = () => {
            held = false;
            if (cancelled) return;
            recordWakeLockRelease();
            setWakeLockStatus("released_by_browser");
            if (releaseReacquires >= MAX_RELEASE_REACQUIRES) {
                reportUnavailable("released");
                return;
            }
            releaseReacquires += 1;
            void acquire();
        };

        const onVisibilityChange = () => {
            if (document.visibilityState !== "visible") return;
            releaseReacquires = 0;
            void acquire();
        };

        void acquire();
        document.addEventListener("visibilitychange", onVisibilityChange);

        return () => {
            cancelled = true;
            document.removeEventListener("visibilitychange", onVisibilityChange);
            if (sentinel) {
                held = false;
                releaseSentinel(sentinel);
                sentinel = null;
            }
        };
    }, [active]);
};

export default useScreenWakeLock;
